import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../Config.js';
import styles from './SchoolSalesReport.module.css';
import Select from "react-select";
import { exportToExcel } from '../Export/exportToExcel.js';

const SalesSchoolReport = () => {
    const user = JSON.parse(sessionStorage.getItem('user'));
    const storeId = user?.storeId;

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [popupData, setPopupData] = useState(null);
    const [popupVisible, setPopupVisible] = useState(false);
    const [userList, setUserList] = useState([]);
    const [userId, setUserId] = useState('ALL');
    const [allSchools, setAllSchools] = useState([]);
    const [selectedSchool, setSelectedSchool] = useState('');
    const selectedSchoolRef = useRef(null);

    // Headers for Export - Detailed Report
    const popupHeaders = [
        { label: "Item Barcode ID", key: "itemBarcodeID" },
        { label: "Item Category", key: "itemCategory" },
        { label: "Item Code", key: "itemCode" },
        { label: "Description", key: "description" },
        { label: "Type", key: "itemType" },
        { label: "Color", key: "itemColor" },
        { label: "Size", key: "itemSize" },
        { label: "Price", key: "price" },
        { label: "Sell Price", key: "sellPrice" },
        { label: "Quantity", key: "totalQuantity" },
        { label: "Total Amount", key: "totalAmount" },
        { label: "Bill Type", key: "billType" }
    ];

    // Headers for Export - Master Table
    const masterHeaders = [
        { label: "School", key: "schoolName" },
        { label: "School Sales", key: "schoolSales" },
        { label: "Other Sales", key: "otherSales" },
        { label: "Total Sales", key: "sales" }
    ];

    // Dynamic filename for detailed report
    const getExportFilename = () => {
        let school = selectedSchool || "School Sales Report";

        if (!startDate || !endDate) {
            return school;
        }
        return `${school}_${startDate}_to_${endDate}`;
    };

    // Dynamic filename for master table
    const getMasterExportFilename = () => {
        if (!startDate || !endDate) {
            return "School_Sales_Summary";
        }
        return `School_Sales_Summary_${startDate}_to_${endDate}`;
    };

    // Handle Excel Export for detailed popup
    const handleExportToExcel = () => {
        if (popupData && popupData.length > 0) {
            exportToExcel(popupHeaders, popupData, getExportFilename());
        }
    };

    // Handle Excel Export for master table
    const handleExportMasterToExcel = () => {
        if (reportData && reportData.length > 0) {
            exportToExcel(masterHeaders, reportData, getMasterExportFilename());
        } else {
            alert('No data available to export');
        }
    };

    const fetchAllSchools = async () => {
        try {
            const user = JSON.parse(sessionStorage.getItem('user'));
            const storeId = user?.storeId;
            const response = await axios.get(`${API_BASE_URL}/user/filter/getSchoolNameandCode`, {
                params: { storeId }
            });

            if (Array.isArray(response.data)) {
                const schoolOptions = response.data.map((school) => ({
                    value: `${school.schoolName} (${school.schoolCode})`,
                    label: `${school.schoolName} (${school.schoolCode})`,
                    schoolName: school.schoolName,
                    schoolCode: school.schoolCode,
                }));
                setAllSchools(schoolOptions);
            }
        } catch (error) {
            console.error('Error fetching schools:', error);
        }
    };

    useEffect(() => {
        fetchAllSchools();
    }, []);

    const fetchSalesReport = async () => {
        try {
            setLoading(true);
            setError(null);

            let apiUrl;

            if (selectedSchool != null && selectedSchool.trim() !== '') {
                apiUrl = `${API_BASE_URL}/sales/report/date/schoolList?startDate=${startDate}&endDate=${endDate}&school=${encodeURIComponent(selectedSchool.trim())}&userId=${userId}&storeId=${storeId}`;
            } else {
                apiUrl = `${API_BASE_URL}/sales/report/school_name?startDate=${startDate}&endDate=${endDate}&storeId=${storeId}&userId=${userId}`;
            }

            const response = await fetch(apiUrl);

            if (!response.ok) {
                throw new Error('Failed to fetch data');
            }

            const data = await response.json();
            const sortedData = data.sort((a, b) => b.sales - a.sales);
            setReportData(sortedData);
        } catch (err) {
            console.log(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const today = new Date();
        const formatted = today.toISOString().split('T')[0];
        setEndDate(formatted);
    }, []);

    const fetchDetailedReport = async (schoolName) => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}/sales/report/schoolSalesSummary?schoolName=${schoolName}&startDate=${startDate}&endDate=${endDate}&storeId=${storeId}`);
            if (!response.ok) throw new Error('Failed to fetch detailed report');

            const data = await response.json();
            setPopupData(data);
            setPopupVisible(true);
        } catch (err) {
            console.log(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleFetch = () => {
        if (startDate && endDate && storeId) {
            fetchSalesReport();
        } else {
            alert('Please select start and end dates');
        }
    };

    const handleSelectChange = (selectedOption) => {
        setSelectedSchool(selectedOption ? selectedOption.schoolName : '');
    };

    const handleClearSchool = () => setSelectedSchool('');

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/store/getAllUserByStore?storeId=${storeId}`);
                const data = await response.json();
                const options = [{ value: "ALL", label: "ALL" }, ...data.map(u => ({ value: u, label: u }))];
                setUserList(options);
            } catch (error) {
                console.error("Error fetching users:", error);
            }
        };

        fetchUser();
    }, [API_BASE_URL, storeId]);

    const handleView = (schoolName) => fetchDetailedReport(schoolName);

    const closePopup = () => {
        setPopupVisible(false);
        setPopupData(null);
    };

    return (
        <div className={styles.salesSchoolReport}>
            <h2 className={styles.salesReportTitle}>Sales School Summary</h2>

            <div className={styles.salesDateInputs}>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={styles.salesInputDate} />
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={styles.salesInputDate} />

                <div className={styles.schoolReportInput}>
                    <Select
                        className={styles.schoolReportSelectContainer}
                        options={allSchools}
                        ref={selectedSchoolRef}
                        value={allSchools.find(s => s.schoolName === selectedSchool) || null}
                        onChange={handleSelectChange}
                        placeholder="Select a school"
                        styles={{ control: (base) => ({ ...base, width: '200px' }) }}
                        filterOption={(option, inputValue) =>
                            option.data.schoolName.toLowerCase().includes(inputValue.toLowerCase()) ||
                            option.data.schoolCode.toLowerCase().includes(inputValue.toLowerCase())
                        }
                    />
                </div>

                <div>
                    <Select
                        options={userList}
                        value={userList.find(option => option.value === userId)}
                        onChange={(selectedOption) => setUserId(selectedOption.value)}
                        placeholder="Select a user..."
                        isSearchable
                    />
                </div>

                <button onClick={handleFetch} className={styles.salesFetchBtn}>Fetch Report</button>

                {/* Export Master Table Button */}
                {!loading && reportData.length > 0 && (
                    <button onClick={handleExportMasterToExcel} className={styles.salesExportBtn}>
                        Export to Excel
                    </button>
                )}

                {selectedSchool && (
                    <button onClick={handleClearSchool} className={styles.schoolReportClearBtn}>Clear School</button>
                )}
            </div>

            {loading && <p className={styles.salesLoadingMessage}>Loading...</p>}
            {error && <p className={styles.salesErrorMessage}>{error}</p>}

            {!loading && reportData.length > 0 && (
                <div className={styles.salesReportTableWrapper}>
                    <table className={styles.salesReportTable}>
                        <thead>
                            <tr>
                                <th>School</th>
                                <th>School Sales</th>
                                <th>Other Sales</th>
                                <th>Total Sales</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reportData.map((report, index) => (
                                <tr key={index}>
                                    <td>{report.schoolName}</td>
                                    <td>{report.schoolSales}</td>
                                    <td>{report.otherSales}</td>
                                    <td>{report.sales}</td>
                                    <td>
                                        <button className={styles.salesViewBtn} onClick={() => handleView(report.schoolName)}>
                                            View
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {!loading && reportData.length > 0 && (
                <>
                    <div className={styles.schoolTotalSale}>
                        <h4>Total Sales:{reportData.reduce((t, r) => t + r.sales, 0)}</h4>
                    </div>
                </>
            )}

            {/* POPUP WITH EXCEL EXPORT BUTTON */}
            {popupVisible && popupData && (
                <div className={styles.salesSchoolReportPopupOverlay}>
                    <div className={styles.salesSchoolReportPopupContent}>
                        <h3>Sales Report Details</h3>

                        <div className={styles.salesDetailTableContainer}>
                            <table className={styles.salesDetailTable}>
                                <thead>
                                    <tr>
                                        <th>Item Barcode ID</th>
                                        <th>Item Category</th>
                                        <th>Item Code</th>
                                        <th>Description</th>
                                        <th>Type</th>
                                        <th>Color</th>
                                        <th>Size</th>
                                        <th>Price</th>
                                        <th>Sell Price</th>
                                        <th>Quantity</th>
                                        <th>Total Amount</th>
                                        <th>Bill Type</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {popupData.map((item, index) => (
                                        <tr key={index}>
                                            <td>{item.itemBarcodeID}</td>
                                            <td>{item.itemCategory}</td>
                                            <td>{item.itemCode}</td>
                                            <td>{item.description}</td>
                                            <td>{item.itemType}</td>
                                            <td>{item.itemColor}</td>
                                            <td>{item.itemSize}</td>
                                            <td>{item.price}</td>
                                            <td>{item.sellPrice}</td>
                                            <td>{item.totalQuantity}</td>
                                            <td>{item.totalAmount}</td>
                                            <td>{item.billType}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Excel Export and Close Button */}
                        <div className={styles.popupButtonContainer}>
                            <button onClick={handleExportToExcel} className={styles.salesExportBtn}>
                                Export to Excel
                            </button>
                            <button onClick={closePopup} className={styles.salesSchoolReportPopupCloseBtn}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default SalesSchoolReport;